import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete } from 'lucide-react';
import { cn } from '@tailoring-crm/ui';

/**
 * Four digits, entered on a keypad drawn on the screen.
 *
 * A tailor signs in with a PIN a dozen times a day on a shop phone, often with fabric dust or
 * chalk on their hands. The on-screen keypad is not decoration: the software keyboard on a
 * mid-range Android takes a beat to appear, covers half the screen, and puts the digits in a
 * row along the top at about a third of the size of the buttons below. Big, fixed, always-there
 * targets are the difference between two seconds and ten.
 *
 * The dots above the pad show progress without showing the PIN — the phone is on a counter
 * that customers stand at.
 */
export interface PinPadProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  label: string;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export function PinPad({
  value,
  onChange,
  onComplete,
  length = 4,
  disabled = false,
  invalid = false,
  label,
}: PinPadProps): ReactElement {
  const { t } = useTranslation();

  const press = (digit: string): void => {
    if (disabled || value.length >= length) return;
    const next = value + digit;
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };

  const backspace = (): void => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  };

  return (
    <div className="flex flex-col gap-4">
      <div
        role="status"
        aria-live="polite"
        aria-label={t('auth.pinEnteredCount', { count: value.length, total: length })}
        className="flex justify-center gap-3"
      >
        {Array.from({ length }, (_unused, index) => (
          <span
            key={index}
            aria-hidden="true"
            className={cn(
              'size-4 rounded-full border-2 transition-colors',
              index < value.length ? 'border-primary bg-primary' : 'border-input',
              invalid && 'border-destructive',
            )}
          />
        ))}
      </div>

      {/*
        A hidden input carries the value for the form and for password managers, and gives the
        field a real label. It is readonly rather than disabled so it still submits: everything
        is typed on the pad below, and a software keyboard opening over it would defeat the
        entire point.
      */}
      <input
        type="password"
        inputMode="numeric"
        autoComplete="off"
        readOnly
        aria-label={label}
        aria-invalid={invalid}
        value={value}
        className="sr-only"
        tabIndex={-1}
      />

      <div
        role="group"
        aria-label={label}
        className="mx-auto grid w-full max-w-xs grid-cols-3 gap-3"
      >
        {KEYS.map((digit) => (
          <PadButton key={digit} label={digit} disabled={disabled} onPress={() => press(digit)} />
        ))}
        <span aria-hidden="true" />
        <PadButton label="0" disabled={disabled} onPress={() => press('0')} />
        <button
          type="button"
          disabled={disabled || value.length === 0}
          onClick={backspace}
          aria-label={t('auth.pinDelete')}
          className="flex h-14 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
        >
          <Delete aria-hidden="true" className="size-6" />
        </button>
      </div>
    </div>
  );
}

function PadButton({
  label,
  disabled,
  onPress,
}: {
  label: string;
  disabled: boolean;
  onPress: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPress}
      className="h-14 rounded-lg border border-border bg-background text-2xl font-semibold tabular-nums transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40"
    >
      {label}
    </button>
  );
}
